import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";

/** PTE-STOCK-0001 — inline typeahead for the Stock Register Add/Edit form.
 * Shows existing Stock Register values for the given field (part number,
 * keyword, alternate part no., NHA part no., supplier, store) as the user
 * types, so an existing value is reused instead of a near-duplicate variant
 * being created. Free-text entry always still works. */
export function StockTypeahead({ field, value, onChange, placeholder, testid }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const v = (value || "").trim();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/stock-items/lookup", { params: { field, q: v } });
        setMatches(r.data.matches || []);
      } catch { setMatches([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [value, field]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const v = (value || "").trim();

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div className="input" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px" }}>
        <Search size={13} color="var(--text-3)" />
        <input
          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 13 }}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {open && (
        <div className="card" style={{ position: "absolute", zIndex: 30, top: 40, left: 0, right: 0, maxHeight: 200, overflowY: "auto", padding: 4 }}
          data-testid={`${testid}-dropdown`}>
          {loading && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
          {!loading && matches.length === 0 && (
            <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }} data-testid={`${testid}-no-matches`}>
              No existing match{v ? ` for "${v}"` : ""} — free text is fine.
            </div>
          )}
          {!loading && matches.map((m) => (
            <button key={m.value} type="button" className="nav-item"
              style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onClick={() => { onChange(m.value); setOpen(false); }}
              data-testid={`${testid}-opt-${m.value}`}>
              <span className="t-strong" style={{ fontSize: 13 }}>{m.value}</span>
              <span className="mono t-mut" style={{ fontSize: 11 }}>{m.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
